import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, Phone, MapPin, Building2, Share2 } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { auth, clients, clientService, getStoredUser } from '../api';
import { PortalUploadsPanel } from '../components/client/PortalUploadsPanel';
import { HealthBreakdown, RATING_STYLES } from '../components/client/HealthBadge';
import { DocumentsPanel } from '../components/client/DocumentsPanel';
import { SignoffsPanel } from '../components/client/SignoffsPanel';
import { ClientSarsPanel } from '../components/client/ClientSarsPanel';
import { PayrollPanel } from '../components/payroll/PayrollPanel';

const Overview = ({ client, health }) => {
  const s = health && RATING_STYLES[health.rating];
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="rounded-xl bg-white border border-stone-200 p-5 lg:col-span-1" data-testid="client-health-card">
        <div className="text-xs uppercase tracking-wider font-semibold text-stone-500">Health score</div>
        {health ? (
          <>
            <div className="mt-2 flex items-end gap-3">
              <span className="font-serif text-5xl font-semibold text-stone-900" data-testid="client-health-score">{health.score}</span>
              <span className={`mb-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ${s.pill}`} data-testid="client-health-rating"><span className={`h-2 w-2 rounded-full ${s.dot}`}/>{s.label}</span>
            </div>
            <div className="mt-5"><HealthBreakdown health={health}/></div>
          </>
        ) : <div className="mt-3 text-sm text-stone-500">Calculating…</div>}
      </div>
      <div className="rounded-xl bg-white border border-stone-200 p-5 lg:col-span-2 space-y-3 text-sm">
        <div className="text-xs uppercase tracking-wider font-semibold text-stone-500">Client details</div>
        <div className="flex items-center gap-2 text-stone-700"><Building2 size={14} className="text-emerald-800"/> {client.entityType || 'Entity not set'}{client.registrationNumber && ` · Reg ${client.registrationNumber}`}</div>
        <div className="flex items-center gap-2 text-stone-700"><Mail size={14} className="text-emerald-800"/> {client.contact || 'No email'}</div>
        <div className="flex items-center gap-2 text-stone-700"><Phone size={14} className="text-emerald-800"/> {client.contactPhone || 'No phone'}</div>
        <div className="flex items-center gap-2 text-stone-700"><MapPin size={14} className="text-emerald-800"/> {client.address || 'No address'}</div>
        {client.portalToken && (
          <a href={`/portal/${client.portalToken}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-emerald-800 hover:underline" data-testid="client-portal-link"><Share2 size={14}/> Open client portal</a>
        )}
      </div>
    </div>
  );
};

export default function ClientProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [user, setUser] = useState(getStoredUser());
  const [client, setClient] = useState(null);
  const [health, setHealth] = useState(null);
  const [missing, setMissing] = useState(false);

  const loadHealth = () => clientService.health().then((h) => setHealth(h[id] || null)).catch(() => {});
  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    Promise.all([auth.me().catch(() => null), clients.list()]).then(([me, cl]) => {
      if (me) setUser(me);
      const c = cl.find((x) => x.id === id);
      if (c) setClient(c); else setMissing(true);
    });
    loadHealth();
    // eslint-disable-next-line
  }, [id]);

  const tab = new URLSearchParams(window.location.search).get('tab') || 'overview';

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Clients"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <Link to="/dashboard/clients" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800" data-testid="back-to-clients"><ArrowLeft size={14}/> All clients</Link>
          {missing && <div className="rounded-xl bg-white border border-stone-200 p-10 text-center text-stone-500">Client not found.</div>}
          {!client && !missing && <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading client…</div>}
          {client && (
            <>
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900" data-testid="client-profile-name">{client.name}</h1>
                  <p className="mt-1 text-stone-600 text-sm">{client.entityType || 'Client'} · everything you need to fully service this client</p>
                </div>
                <Button variant="outline" onClick={() => nav(`/dashboard/clients?open=${client.id}`)} data-testid="client-edit-btn">Edit client</Button>
              </div>
              <Tabs defaultValue={tab}>
                <TabsList data-testid="client-profile-tabs">
                  <TabsTrigger value="overview" data-testid="client-tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="client-tab-documents">Documents</TabsTrigger>
                  <TabsTrigger value="uploads" data-testid="client-tab-uploads">Client uploads</TabsTrigger>
                  <TabsTrigger value="signoffs" data-testid="client-tab-signoffs">Sign-offs</TabsTrigger>
                  <TabsTrigger value="sars" data-testid="client-tab-sars">SARS</TabsTrigger>
                  <TabsTrigger value="payroll" data-testid="client-tab-payroll">Payroll</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="mt-6"><Overview client={client} health={health}/></TabsContent>
                <TabsContent value="documents" className="mt-6"><DocumentsPanel clientId={client.id} onChange={loadHealth}/></TabsContent>
                <TabsContent value="uploads" className="mt-6"><PortalUploadsPanel clientId={client.id}/></TabsContent>
                <TabsContent value="signoffs" className="mt-6"><SignoffsPanel clientId={client.id}/></TabsContent>
                <TabsContent value="sars" className="mt-6"><ClientSarsPanel clientId={client.id}/></TabsContent>
                <TabsContent value="payroll" className="mt-6"><PayrollPanel client={client} onEnabled={() => setClient({ ...client, payrollEnabled: true })}/></TabsContent>
              </Tabs>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
